import React from "react";
import { Checkbox, Input, Output } from "@ds/ds-snam/react";
import { IBooking } from "dataModels/b2b";
import { Col, Row } from "react-grid-system";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { CalendarSlotStatus, DsLayout } from "shared/constants";
import { RootState } from "state/appStore";

type BookingBioData = Pick<IBooking, "bioFlagDisabled" | "bioFlagStatus" | "percentageBioLng">;

export const isBookingBioPercentageValid = (data: BookingBioData): boolean => {
    const { percentageBioLng } = data;
    return data.bioFlagStatus === false || percentageBioLng === undefined ||
        (Number.isFinite(percentageBioLng) && percentageBioLng >= 0 && percentageBioLng <= 100);
};

interface IBookingBioFieldsProps {
    bookingData: BookingBioData & Pick<IBooking, "status">,
    onChange?: (data: Pick<IBooking, "bioFlagStatus" | "percentageBioLng">) => void,
}

const BookingBioFields: React.FC<IBookingBioFieldsProps> = ({ bookingData, onChange }) => {
    const { t } = useTranslation(["b2b"]);
    const layoutMode = useSelector((state: RootState) => state.dsLayout.mode);
    const bioData = bookingData;
    const isReadOnly = !onChange || bookingData.status === CalendarSlotStatus.CLOSED;
    const isBioDisabled = isReadOnly || bioData.bioFlagDisabled !== false;
    const isPercentageDisabled = isBioDisabled || bioData.bioFlagStatus !== true;
    const percentageBioLng = bioData.bioFlagStatus === false ? 0 : bioData.percentageBioLng;
    const isPercentageValid = isBookingBioPercentageValid(bioData);
    const columnWidth = onChange ? 4 : 6;
    const titleStyle: React.CSSProperties = {
        fontSize: '80%',
        fontWeight: 'bold',
        marginBottom: '5px',
        color: layoutMode === DsLayout.DARK_MODE ? "white" : "black",
    };
    // Matches the DS Input field height so checkboxes align with the percentage input.
    const checkboxWrapperStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', minHeight: '48px' };

    return (
        <Row style={{ marginBottom: "12px" }}>
            <Col xs={12} md={columnWidth}>
                {!onChange ? (
                    <Output
                        outputTitle={t("booking.bookingForm.bio.statusTitle")}
                        outputText={bioData.bioFlagStatus ? t("booking.bookingForm.bio.yes") : t("booking.bookingForm.bio.no")}
                        style={{ marginBottom: "16px" }}
                    />
                ) : (
                    <>
                <div style={titleStyle}>{t("booking.bookingForm.bio.statusTitle")}</div>
                <div style={checkboxWrapperStyle}>
                <Checkbox
                    checkboxId="bioFlagStatus"
                    checkboxStatus="default"
                    checkboxLabel="Bio LNG"
                    elementResetPair=""
                    isDisabled={isBioDisabled}
                    isChecked={bioData.bioFlagStatus}
                    isRequired={false}
                    onCheckboxChange={(event: Event) => {
                        if (!isBioDisabled) {
                            const { checked } = (event as CustomEvent<{ checkbox: { checked: boolean } }>).detail.checkbox;
                            onChange?.({
                                bioFlagStatus: checked,
                                percentageBioLng: checked ? bioData.percentageBioLng : 0,
                            });
                        }
                    }}
                />
                </div>
                    </>
                )}
            </Col>
            <Col xs={12} md={columnWidth}>
                {!onChange ? (
                    <Output
                        outputTitle={t("booking.bookingForm.bio.percentage")}
                        outputText={percentageBioLng !== undefined && Number.isFinite(percentageBioLng) ? `${percentageBioLng} %` : "-"}
                        style={{ marginBottom: "16px" }}
                    />
                ) : (
                    <>
                        <Input
                            inputId="percentageBioLng"
                            inputLabel={t("booking.bookingForm.bio.percentage")}
                            inputValue={percentageBioLng !== undefined && Number.isFinite(percentageBioLng) ? percentageBioLng.toString() : ""}
                            inputDescription={isPercentageValid ? "%" : t("booking.bookingForm.bio.percentageError")}
                            inputStatus={isPercentageValid ? "default" : "error"}
                            inputPlaceholder=""
                            input-event-type="single_character"
                            isNumberVariant={true}
                            minValue={0}
                            maxValue={100}
                            isDisabled={isPercentageDisabled}
                            onInputChange={(event: Event) => {
                                if (!isPercentageDisabled) {
                                    const { value } = (event as CustomEvent<{ input: { value: string } }>).detail.input;
                                    onChange?.({
                                        bioFlagStatus: bioData.bioFlagStatus,
                                        percentageBioLng: Number(value.replace(",", ".")),
                                    });
                                }
                            }}
                        />
                    </>
                )}
            </Col>
        </Row>
    );
};

export default BookingBioFields;
